import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import HomePage from "@/pages/HomePage";
import GalleryPage from "@/pages/GalleryPage";
import SuppliersPage from "@/pages/SuppliersPage";
import CareersPage from "@/pages/CareersPage";
import LoginPage from "@/pages/LoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import DashboardOverview from "@/pages/admin/DashboardOverview";
import InventoryPage from "@/pages/admin/InventoryPage";
import EmployeesPage from "@/pages/admin/EmployeesPage";
import ProjectsPage from "@/pages/admin/ProjectsPage";
import RequestsPage from "@/pages/admin/RequestsPage";
import DocumentsPage from "@/pages/admin/DocumentsPage";
import AttendancePage from "@/pages/admin/AttendancePage";
import InvoicesPage from "@/pages/admin/InvoicesPage";
import InvoiceDetailPage from "@/pages/admin/InvoiceDetailPage";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground" data-testid="auth-loading">
        Verifying session…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  // Auto-redirect any preview / emergent.host hostname to the production domain.
  // This runs only in the browser and preserves the path + query + hash.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname;
    const isEmergentHost =
      host.endsWith(".preview.emergentagent.com") ||
      host.endsWith(".emergent.host");
    if (isEmergentHost) {
      const target =
        "https://www.mnh-ksa.com" +
        window.location.pathname +
        window.location.search +
        window.location.hash;
      window.location.replace(target);
    }
  }, []);

  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/suppliers" element={<SuppliersPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardOverview />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="attendance" element={<AttendancePage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                <Route path="compliance" element={<DocumentsPage />} />
                <Route path="requests" element={<RequestsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
